import { FormEvent, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import { useAppSelector } from '../../hooks/redux';
import StatusPill from '../../components/StatusPill';
import { Card, EmptyState, Field, LoadingRows, Notice, PageHeader, Toggle } from '../../components/ui/Page';
import { ago, companyDisplayName, date, titleCase } from '../../lib/format';
import { countryName } from '../../lib/countries';
import { Avatar, setUserAccess, setUserPassword } from './index';
import { setCompanyAccess } from '../Companies';
import CompanyLogo from '../../components/CompanyLogo';

type Creator = { id: string; name: string | null; email: string; phone: string | null } | null;

type UserDetail = {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  role: 'ADMIN' | 'CUSTOMER';
  isActive: boolean;
  disabledAt: string | null;
  lastLoginAt: string | null;
  mustChangePassword: boolean;
  createdAt: string;
  createdBy: Creator;
  customerCompanies: {
    id: string;
    name: string;
    logicalName?: string | null;
    legalName: string | null;
    email: string;
    phone: string | null;
    countryCode: string;
    hasLogo?: boolean;
    sector: string | null;
    status: string;
    isActive: boolean;
    disabledAt: string | null;
    fundingAmount: string | null;
    createdAt: string;
    assignedAdmin: Creator;
    ratings: { version: number; grade: string; score: number; status: string }[];
    _count: { documents: number };
  }[];
  assignedCompanies: { id: string; name: string; logicalName?: string | null; countryCode: string; status: string; isActive: boolean; hasLogo?: boolean }[];
};

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 text-[13px]">
      <span className="text-[#a77d92]">{label}</span>
      <span className="text-right font-medium text-[#3b0a22]">{value}</span>
    </div>
  );
}

export default function UserDetailPage() {
  const { id } = useParams();
  const me = useAppSelector((s) => s.auth.user);
  const { data, loading, error, reload } = useApi<{ user: UserDetail }>(id ? `/admin/users/${id}` : null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ password: '', confirm: '' });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const user = data?.user;

  const run = async (key: string, fn: () => Promise<boolean>, ok: string) => {
    setBusy(key);
    setNotice(null);
    try {
      if (await fn()) {
        setNotice({ tone: 'success', text: ok });
        await reload();
      }
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Update failed.') });
    } finally {
      setBusy(null);
    }
  };

  const onChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return;
    setPasswordError(null);
    setNotice(null);
    if (passwordForm.password.length < 10) {
      setPasswordError('Password must be at least 10 characters.');
      return;
    }
    if (!/[A-Za-z]/.test(passwordForm.password) || !/[0-9]/.test(passwordForm.password)) {
      setPasswordError('Password must contain a letter and a number.');
      return;
    }
    if (passwordForm.password !== passwordForm.confirm) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setBusy('password');
    try {
      await setUserPassword(user.id, passwordForm.password);
      setPasswordForm({ password: '', confirm: '' });
      setChangingPassword(false);
      setNotice({ tone: 'success', text: `Password updated for ${user.name || user.email}.` });
      await reload();
    } catch (e) {
      setPasswordError(apiError(e, 'Could not change the password.'));
    } finally {
      setBusy(null);
    }
  };

  if (loading && !user) return <LoadingRows rows={6} />;
  if (!user) return <Notice tone="error">{error || 'User not found.'}</Notice>;

  const isSelf = user.id === me?.id;
  const companies = user.customerCompanies;

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'Users', to: '/users' }, { label: user.name || user.email }]}
        title={
          <span className="flex items-center gap-2.5">
            {user.name || user.email}
            <StatusPill label={user.isActive ? 'Active' : 'Disabled'} tone={user.isActive ? 'green' : 'rose'} />
          </span>
        }
        description={user.role === 'ADMIN' ? 'Micus staff account.' : `Customer account with ${companies.length} ${companies.length === 1 ? 'company' : 'companies'}.`}
      />

      {notice && (
        <Notice tone={notice.tone} onClose={() => setNotice(null)} className="mb-4">
          {notice.text}
        </Notice>
      )}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <div className="space-y-4">
          <Card>
            <div className="flex items-center gap-3">
              <Avatar name={user.name} email={user.email} muted={!user.isActive} />
              <div className="min-w-0">
                <p className="truncate font-semibold">{user.name || 'Unnamed'}</p>
                <p className="truncate text-xs text-[#a77d92]">{user.email}</p>
              </div>
            </div>
            <div className="mt-3 divide-y divide-[#fbeef4]">
              <InfoRow label="Role" value={user.role === 'ADMIN' ? 'Admin' : 'Customer'} />
              <InfoRow label="Phone" value={user.phone || '—'} />
              <InfoRow label="Joined" value={date(user.createdAt)} />
              <InfoRow label="Last sign-in" value={ago(user.lastLoginAt)} />
              {user.mustChangePassword && <InfoRow label="Password" value={<span className="text-[#b45309]">Temporary</span>} />}
              {user.createdBy && <InfoRow label="Created by" value={user.createdBy.name || user.createdBy.email} />}
            </div>
          </Card>

          <Card title="Account access">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[13px] font-semibold">{user.isActive ? 'Can sign in' : 'Sign-in blocked'}</p>
                <p className="mt-0.5 text-xs text-[#a77d92]">
                  {isSelf
                    ? 'You cannot disable your own account.'
                    : user.isActive
                      ? 'Disabling signs the user out everywhere.'
                      : `Disabled ${date(user.disabledAt)}.`}
                </p>
              </div>
              <Toggle
                checked={user.isActive}
                disabled={isSelf || busy === 'user'}
                label="Account access"
                onChange={(v) => void run('user', () => setUserAccess(user, v), `${user.name || user.email} ${v ? 'enabled' : 'disabled'}.`)}
              />
            </div>
            <div className="mt-4 border-t border-[#fbeef4] pt-4">
              <p className="text-[13px] font-semibold">Password</p>
              <p className="mt-0.5 text-xs text-[#a77d92]">
                {user.mustChangePassword
                  ? 'They still have a temporary password from account setup.'
                  : 'Set a new password for this account. They can sign in with it immediately.'}
              </p>
              {changingPassword ? (
                <form onSubmit={onChangePassword} className="mt-3 space-y-3">
                  <Field label="New password" htmlFor="u-new-password">
                    <input
                      id="u-new-password"
                      type="password"
                      required
                      minLength={10}
                      maxLength={128}
                      autoComplete="new-password"
                      className="mz-input"
                      value={passwordForm.password}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, password: e.target.value }))}
                    />
                  </Field>
                  <Field label="Confirm password" htmlFor="u-confirm-password">
                    <input
                      id="u-confirm-password"
                      type="password"
                      required
                      minLength={10}
                      maxLength={128}
                      autoComplete="new-password"
                      className="mz-input"
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, confirm: e.target.value }))}
                    />
                  </Field>
                  <p className="text-[11px] text-[#a77d92]">At least 10 characters, with a letter and a number.</p>
                  {passwordError && <Notice tone="error">{passwordError}</Notice>}
                  <div className="flex flex-wrap gap-2">
                    <button type="submit" className="mz-btn-primary" disabled={busy === 'password'}>
                      {busy === 'password' ? 'Saving…' : 'Save password'}
                    </button>
                    <button
                      type="button"
                      className="mz-btn-ghost"
                      disabled={busy === 'password'}
                      onClick={() => {
                        setChangingPassword(false);
                        setPasswordForm({ password: '', confirm: '' });
                        setPasswordError(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button type="button" className="mz-btn-ghost mt-3" onClick={() => setChangingPassword(true)}>
                  Change password
                </button>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Companies" subtitle="Disabled companies are hidden from the customer's workspace but stay visible to Micus staff." padded={false}>
            {companies.length === 0 ? (
              <EmptyState title="No companies" description={user.role === 'ADMIN' ? 'Admins do not own companies.' : 'This customer has not registered a company yet.'} />
            ) : (
              <div className="overflow-x-auto">
                <table className="mz-table min-w-[640px]">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Stage</th>
                      <th>Rating</th>
                      <th>Docs</th>
                      <th className="text-right">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((c) => {
                      const r = c.ratings[0];
                      return (
                        <tr key={c.id} className={c.isActive ? '' : 'opacity-60'}>
                          <td>
                            <div className="flex items-center gap-2">
                              <CompanyLogo companyId={c.id} name={companyDisplayName(c)} hasLogo={c.hasLogo} className="h-8 w-8" />
                              <div className="min-w-0">
                                <p className="truncate font-semibold">{companyDisplayName(c)}</p>
                                <p className="truncate text-xs text-[#a77d92]">
                                  {countryName(c.countryCode)}
                                  {c.sector ? ` · ${c.sector}` : ''}
                                  {c.assignedAdmin ? ` · ${c.assignedAdmin.name || c.assignedAdmin.email}` : ''}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <StatusPill label={titleCase(c.status)} tone={c.status === 'FUNDED' ? 'green' : c.status === 'INVITED' ? 'amber' : 'pink'} />
                          </td>
                          <td className="text-[13px]">{r ? <span className="font-semibold">{r.grade}</span> : <span className="text-[#c9a3b5]">—</span>}{r && <span className="text-xs text-[#a77d92]"> · {r.score}/100</span>}</td>
                          <td className="text-[13px] text-[#9a6b82]">{c._count.documents}</td>
                          <td className="text-right">
                            <Toggle
                              size="sm"
                              checked={c.isActive}
                              disabled={busy === c.id}
                              label={c.isActive ? 'Disable company' : 'Enable company'}
                              onChange={(v) => void run(c.id, () => setCompanyAccess(c, v), `${companyDisplayName(c)} ${v ? 'enabled' : 'disabled'}.`)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {user.role === 'ADMIN' && (
            <Card title="Assigned companies" subtitle="Companies this analyst owns in Consult." padded={false}>
              {user.assignedCompanies.length === 0 ? (
                <EmptyState title="Nothing assigned" />
              ) : (
                <ul className="divide-y divide-[#fbeef4]">
                  {user.assignedCompanies.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13px]">
                      <span className="flex min-w-0 items-center gap-2">
                        <CompanyLogo companyId={c.id} name={companyDisplayName(c)} hasLogo={c.hasLogo} className="h-8 w-8" />
                        <span className={`truncate font-medium ${c.isActive ? '' : 'text-[#b99aaa] line-through'}`}>{companyDisplayName(c)}</span>
                      </span>
                      <StatusPill label={titleCase(c.status)} tone="slate" />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          <p className="text-xs text-[#a77d92]">
            Company files, ratings and fees are handled in <Link to="/companies" className="font-semibold text-[#d6006e] hover:underline">Companies</Link> and in Micus Consult.
          </p>
        </div>
      </div>
    </div>
  );
}
